import * as React from "react";
import { Check, Loader2, ExternalLink, Crown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext.js";
import { usePlan } from "@/contexts/PlanContext.js";
import { apiFetch } from "@/lib/api.js";
import { PLANS, type PlanId, type BillingInterval } from "@homethyst/shared/plans";
import { cn } from "@/lib/utils.js";

interface BillingStatus {
  plan: string;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  currentPeriodEnd: string | null;
}

export default function PricingPage() {
  const { user } = useAuth();
  const { usage, refresh: refreshPlan } = usePlan();
  const [interval, setInterval] = React.useState<BillingInterval>("monthly");
  const [loading, setLoading] = React.useState<string | null>(null);
  const [billingStatus, setBillingStatus] = React.useState<BillingStatus | null>(null);

  React.useEffect(() => {
    apiFetch<BillingStatus>("/api/billing/status").then(setBillingStatus).catch(() => {});
  }, []);

  const handleSubscribe = async (plan: PlanId) => {
    setLoading(plan);
    try {
      const { url } = await apiFetch<{ url: string }>("/api/billing/checkout", {
        method: "POST",
        body: JSON.stringify({ plan, interval }),
      });
      if (url) window.location.href = url;
    } catch (err) {
      console.error("Checkout error:", err);
    } finally {
      setLoading(null);
    }
  };

  const handleManage = async () => {
    setLoading("portal");
    try {
      const { url } = await apiFetch<{ url: string }>("/api/billing/portal", {
        method: "POST",
      });
      if (url) window.location.href = url;
    } catch (err) {
      console.error("Portal error:", err);
    } finally {
      setLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!confirm("Cancel your subscription? You'll keep access until the end of your billing period.")) return;
    setLoading("cancel");
    try {
      await apiFetch("/api/billing/cancel", { method: "POST" });
      refreshPlan();
      const status = await apiFetch<BillingStatus>("/api/billing/status");
      setBillingStatus(status);
    } catch (err) {
      console.error("Cancel error:", err);
    } finally {
      setLoading(null);
    }
  };

  const handleSwitch = async (newInterval: BillingInterval) => {
    setLoading("switch");
    try {
      await apiFetch("/api/billing/switch", {
        method: "POST",
        body: JSON.stringify({ interval: newInterval }),
      });
      setInterval(newInterval);
      refreshPlan();
    } catch (err) {
      console.error("Switch error:", err);
    } finally {
      setLoading(null);
    }
  };

  const currentPlan = usage?.plan ?? "free";

  const plans: { id: PlanId; features: string[] }[] = [
    {
      id: "free",
      features: ["1 home", "10 AI actions/month", "5 photos", "Ad-supported"],
    },
    {
      id: "plus",
      features: ["1 home", "100 AI actions/month", "100 photos", "No ads"],
    },
    {
      id: "pro",
      features: ["Unlimited homes", "Unlimited AI actions", "Unlimited photos", "No ads"],
    },
  ];

  const secondaryBtn = "w-full py-2 rounded-md border border-clay-border text-sm text-body hover:bg-paper transition-colors flex items-center justify-center gap-2 disabled:bg-clay-muted disabled:text-faint disabled:cursor-not-allowed";

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-ink font-headline tracking-[-0.01em]">Choose your plan</h1>
        <p className="text-body mt-2">Upgrade anytime. Cancel anytime.</p>
      </div>

      {/* Interval toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-md bg-clay-muted p-1">
          <button
            onClick={() => setInterval("monthly")}
            className={cn(
              "px-4 py-2 rounded-sm text-sm font-medium transition-colors",
              interval === "monthly" ? "bg-white text-ink" : "text-body",
            )}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval("annual")}
            className={cn(
              "px-4 py-2 rounded-sm text-sm font-medium transition-colors",
              interval === "annual" ? "bg-white text-ink" : "text-body",
            )}
          >
            Annual <span className="text-[0.8125rem] text-success-text ml-1">Save ~17%</span>
          </button>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map(({ id, features }) => {
          const config = PLANS[id];
          const price = interval === "monthly" ? config.monthlyPrice : config.annualPrice;
          const isCurrentPlan = currentPlan === id;
          const isPopular = id === "pro";

          return (
            <div
              key={id}
              className={cn(
                "relative rounded-lg border p-6 flex flex-col transition-colors",
                // Flat-Content Rule: the "most popular" card earns emphasis from a
                // stronger 2px amethyst border, never a resting shadow.
                isPopular ? "border-2 border-primary" : "border border-clay-border",
                isCurrentPlan && "ring-2 ring-primary/30",
              )}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-pill bg-primary text-white text-[0.8125rem] font-semibold">
                  Most Popular
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-[1.0625rem] font-semibold text-ink flex items-center gap-2">
                  {id === "pro" && <Crown className="w-4 h-4 text-primary" />}
                  {config.label}
                </h3>
                <div className="mt-2">
                  <span className="text-3xl font-semibold text-ink tabular-nums">{price === 0 ? "Free" : `$${price}`}</span>
                  {price > 0 && (
                    <span className="text-faint text-sm">
                      /{interval === "monthly" ? "mo" : "yr"}
                    </span>
                  )}
                </div>
              </div>

              <ul className="space-y-2 flex-1 mb-6">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-body">
                    <Check className="w-4 h-4 text-sage-deep shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              {isCurrentPlan ? (
                <div className="space-y-2">
                  <div className="w-full py-2.5 rounded-md bg-clay-muted text-body text-sm font-medium text-center">
                    Current plan
                  </div>
                  {billingStatus?.stripeSubscriptionId && (
                    <button
                      onClick={handleManage}
                      disabled={loading === "portal"}
                      className={secondaryBtn}
                    >
                      {loading === "portal" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ExternalLink className="w-3.5 h-3.5" />
                          Manage subscription
                        </>
                      )}
                    </button>
                  )}
                  {billingStatus?.stripeSubscriptionId && (
                    <button
                      onClick={() => handleSwitch(interval === "monthly" ? "annual" : "monthly")}
                      disabled={loading === "switch"}
                      className={secondaryBtn}
                    >
                      {loading === "switch" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        `Switch to ${interval === "monthly" ? "annual" : "monthly"}`
                      )}
                    </button>
                  )}
                  {billingStatus?.stripeSubscriptionId && (
                    <button
                      onClick={handleCancel}
                      disabled={loading === "cancel"}
                      className="w-full py-2 rounded-md text-sm text-error-text hover:bg-error-wash transition-colors flex items-center justify-center disabled:text-faint disabled:cursor-not-allowed"
                    >
                      {loading === "cancel" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Cancel subscription"
                      )}
                    </button>
                  )}
                </div>
              ) : id === "free" ? (
                billingStatus?.stripeSubscriptionId ? (
                  <button
                    onClick={handleManage}
                    disabled={loading === "portal"}
                    className={secondaryBtn + " py-2.5 font-medium"}
                  >
                    {loading === "portal" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Downgrade via portal"
                    )}
                  </button>
                ) : (
                  <div className="w-full py-2.5 rounded-md bg-paper text-faint text-sm text-center">
                    Free
                  </div>
                )
              ) : (
                <button
                  onClick={() => handleSubscribe(id)}
                  disabled={loading === id}
                  className={cn(
                    "w-full py-2.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:bg-clay-muted disabled:text-faint disabled:cursor-not-allowed",
                    isPopular
                      ? "bg-primary text-white hover:bg-amethyst-ink"
                      : "bg-amethyst-soft text-amethyst-ink hover:bg-clay-muted",
                  )}
                >
                  {loading === id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    `Upgrade to ${config.label}`
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {billingStatus?.currentPeriodEnd && (
        <p className="text-center text-[0.8125rem] text-faint">
          Current period ends{" "}
          {new Date(billingStatus.currentPeriodEnd).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}
